import { formatInstant, resolveDisplayZone, type AdminWaitlistEntry, type WaitlistStatus } from "@checklist/shared";
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { AdminNav } from "../components/admin/AdminNav.js";
import { ErrorBanner } from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";
import { useConfirm } from "../components/ui/ConfirmDialog.js";
import { InlineSpinner } from "../components/ui/InlineSpinner.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { showErrorToast, showSuccessToast } from "../components/ui/Toast.js";
import { useAuth } from "../context/AuthContext.js";
import {
  ApiError,
  approveWaitlist,
  denyWaitlist,
  fetchAdminWaitlist,
  type WaitlistActionResult,
} from "../lib/api.js";

const TABS: { status: WaitlistStatus; label: string }[] = [
  { status: "PENDING", label: "Pending" },
  { status: "APPROVED", label: "Approved" },
  { status: "DENIED", label: "Denied" },
];

const UA_MAX = 80;

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function peopleLabel(count: number): string {
  return count === 1 ? "1 person" : `${count} people`;
}

function reportResults(results: WaitlistActionResult[], action: "approve" | "deny"): void {
  if (action === "approve") {
    const delivered = results.filter((row) => row.status === "approved" && row.emailDelivered);
    const undelivered = results.filter((row) => row.status === "approved" && !row.emailDelivered);
    if (delivered.length > 0) {
      showSuccessToast(
        delivered.length === 1
          ? `Approved ${delivered[0]!.email}. Invitation email sent.`
          : `Approved ${delivered.length}. Invitation emails sent.`
      );
    }
    if (undelivered.length > 0) {
      showErrorToast(
        `Approved ${undelivered.map((row) => row.email).join(", ")} but the invitation email was not delivered.`
      );
    }
  } else {
    const denied = results.filter((row) => row.status === "denied");
    if (denied.length > 0) {
      showSuccessToast(denied.length === 1 ? `Denied ${denied[0]!.email}.` : `Denied ${denied.length}.`);
    }
  }
  const skipped = results.filter((row) => row.status === "skipped");
  if (skipped.length > 0) {
    showErrorToast(
      skipped.length === 1
        ? `Skipped ${skipped[0]!.email}. It was already decided.`
        : `Skipped ${skipped.length}. They were already decided.`
    );
  }
  if (results.length === 0) showErrorToast("Nothing was updated.");
}

function ClusterBadges({ entry }: { entry: AdminWaitlistEntry }) {
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {entry.sameIpCount > 0 && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-950">
          {entry.sameIpCount} same IP
        </span>
      )}
      {entry.sameNetworkCount > 0 && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-950">
          {entry.sameNetworkCount} same network
        </span>
      )}
    </span>
  );
}

export function AdminWaitlistPage() {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.groups.includes("admin"));
  const confirm = useConfirm();
  const [zone] = useState(() => resolveDisplayZone());
  const [status, setStatus] = useState<WaitlistStatus>("PENDING");
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAdminWaitlist(status)
      .then((result) => {
        if (!cancelled) setEntries(result.entries);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setEntries([]);
          setError(err instanceof ApiError ? err.message : "Failed to load access requests");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, status, reloadToken]);

  const selectedEmails = useMemo(
    () => entries.filter((entry) => selected.has(entry.email)).map((entry) => entry.email),
    [entries, selected]
  );
  const allSelected = entries.length > 0 && selectedEmails.length === entries.length;

  function toggle(email: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(entries.map((entry) => entry.email)));
  }

  function changeStatus(next: WaitlistStatus) {
    setStatus(next);
    setSelected(new Set());
  }

  async function run(key: string, action: () => Promise<void>) {
    setPendingKeys((current) => new Set(current).add(key));
    try {
      await action();
      setReloadToken((n) => n + 1);
      setSelected(new Set());
    } catch (err) {
      showErrorToast(err instanceof ApiError ? err.message : "Could not update access requests.");
    } finally {
      setPendingKeys((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  }

  async function onApprove(emails: string[], key: string) {
    if (emails.length === 0) return;
    const ok = await confirm({
      title: emails.length === 1 ? "Email this invitation?" : "Email these invitations?",
      message: `This emails ${peopleLabel(emails.length)} an invitation link.`,
      confirmLabel: "Approve and email",
    });
    if (!ok) return;
    await run(key, async () => {
      const result = await approveWaitlist(emails);
      reportResults(result.results, "approve");
    });
  }

  async function onDeny(emails: string[], key: string, bulk: boolean) {
    if (emails.length === 0) return;
    if (bulk) {
      const ok = await confirm({
        title: `Deny ${emails.length} request${emails.length === 1 ? "" : "s"}?`,
        message: "Denied requests stay on the Denied tab until you approve them.",
        confirmLabel: "Deny",
        destructive: true,
      });
      if (!ok) return;
    }
    await run(key, async () => {
      const result = await denyWaitlist(emails);
      reportResults(result.results, "deny");
    });
  }

  if (!isAdmin) return <Navigate to="/" replace />;

  const canApprove = status === "PENDING" || status === "DENIED";
  const canDeny = status === "PENDING";

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:py-10">
      <PageHeader
        title="Admin · Access requests"
        subtitle="Approve sends one invitation email to each address. IP and browser details are removed when a request is approved."
      />
      <AdminNav />
      <ErrorBanner message={error} />
      <div className="mb-5 inline-flex rounded-lg border border-line bg-surface p-1">
        {TABS.map((tab) => (
          <button
            key={tab.status}
            type="button"
            onClick={() => changeStatus(tab.status)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${status === tab.status ? "bg-brand-600 text-white" : "text-muted hover:text-ink"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {(canApprove || canDeny) && selectedEmails.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">{selectedEmails.length} selected</span>
          {canApprove && (
            <Button
              size="sm"
              className="min-w-28"
              disabled={pendingKeys.has("bulk-approve")}
              aria-busy={pendingKeys.has("bulk-approve")}
              onClick={() => void onApprove(selectedEmails, "bulk-approve")}
            >
              {pendingKeys.has("bulk-approve") ? <InlineSpinner /> : `Approve ${selectedEmails.length}`}
            </Button>
          )}
          {canDeny && (
            <Button
              size="sm"
              variant="danger"
              className="min-w-28"
              disabled={pendingKeys.has("bulk-deny")}
              aria-busy={pendingKeys.has("bulk-deny")}
              onClick={() => void onDeny(selectedEmails, "bulk-deny", true)}
            >
              {pendingKeys.has("bulk-deny") ? <InlineSpinner /> : `Deny ${selectedEmails.length}`}
            </Button>
          )}
        </div>
      )}
      {loading ? (
        <p className="py-12 text-sm text-muted">Loading access requests…</p>
      ) : entries.length === 0 ? (
        <p className="rounded-2xl border border-line bg-surface p-8 text-center text-sm text-muted">
          No {status.toLowerCase()} requests.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-muted-sm">
              <tr>
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label={allSelected ? "Clear selection" : "Select all"}
                  />
                </th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Requested</th>
                <th className="px-3 py-3">IP</th>
                <th className="px-3 py-3">Browser</th>
                <th className="px-3 py-3">Note</th>
                <th className="px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const approveKey = `approve:${entry.email}`;
                const denyKey = `deny:${entry.email}`;
                const approving = pendingKeys.has(approveKey);
                const denying = pendingKeys.has(denyKey);
                return (
                  <tr key={entry.email} className="border-t border-line align-top">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        className="size-4"
                        checked={selected.has(entry.email)}
                        onChange={() => toggle(entry.email)}
                        aria-label={`Select ${entry.email}`}
                      />
                    </td>
                    <td className="px-3 py-3 text-ink">{entry.email}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-muted">
                      {formatInstant(entry.requestedAt, zone, { format: "dateTime", locale: "en-US" })}
                      <div className="text-xs text-muted-sm">
                        {entry.requestCount} request{entry.requestCount === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-muted">
                      {entry.ip ? (
                        <a
                          href={`https://ipinfo.io/${encodeURIComponent(entry.ip)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs text-brand-700 underline-offset-2 hover:underline"
                        >
                          {entry.ip}
                        </a>
                      ) : (
                        <span className="text-xs text-muted-sm">Removed</span>
                      )}
                      <ClusterBadges entry={entry} />
                    </td>
                    <td className="max-w-48 px-3 py-3 text-xs text-muted" title={entry.userAgent}>
                      {entry.userAgent ? truncate(entry.userAgent, UA_MAX) : "—"}
                    </td>
                    <td className="max-w-64 px-3 py-3 whitespace-pre-wrap break-words text-ink">
                      {entry.note ?? ""}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        {canApprove && (
                          <Button
                            size="sm"
                            className="min-w-24"
                            disabled={approving}
                            aria-busy={approving}
                            onClick={() => void onApprove([entry.email], approveKey)}
                          >
                            {approving ? <InlineSpinner /> : "Approve"}
                          </Button>
                        )}
                        {canDeny && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="min-w-24"
                            disabled={denying}
                            aria-busy={denying}
                            onClick={() => void onDeny([entry.email], denyKey, false)}
                          >
                            {denying ? <InlineSpinner /> : "Deny"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
